import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";

const ThemeModeContext = createContext(null);

export function useThemeMode() {
  return useContext(ThemeModeContext);
}

export function ThemeModeProvider({ children }) {
  const [mode, setMode] = useState(() => localStorage.getItem("theme") || "dark");

  useEffect(() => {
    localStorage.setItem("theme", mode);
    document.body.classList.remove("light-mode", "dark-mode");
    document.body.classList.add(mode === "light" ? "light-mode" : "dark-mode");
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => (prev === "dark" ? "light" : "dark"));
  };

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          ...(mode === "dark"
            ? {
                background: {
                  default: "#11151b",
                  paper: "#181d25",
                },
                primary: {
                  main: "#45b8ff",
                },
                secondary: {
                  main: "#9aa4b2",
                },
                text: {
                  primary: "#dbe2ea",
                  secondary: "rgba(219, 226, 234, 0.66)",
                },
                divider: "rgba(255, 255, 255, 0.09)",
              }
            : {
                background: {
                  default: "#f3f5f9",
                  paper: "#ffffff",
                },
                primary: {
                  main: "#0284c7",
                },
                secondary: {
                  main: "#64748b",
                },
                text: {
                  primary: "#161b22",
                  secondary: "rgba(22, 27, 34, 0.66)",
                },
                divider: "rgba(22, 27, 34, 0.10)",
              }),
        },
        shape: {
          borderRadius: 16,
        },
        typography: {
          fontFamily: [
            "Inter",
            "-apple-system",
            "BlinkMacSystemFont",
            "Segoe UI",
            "Roboto",
            "Helvetica Neue",
            "Arial",
            "sans-serif",
          ].join(","),
          ...Object.fromEntries(
            ["h1", "h2", "h3", "h4", "h5", "h6"].map((v) => [
              v,
              {
                fontFamily: "var(--font-display)",
                letterSpacing: "-0.02em",
              },
            ])
          ),
        },
        components: {
          MuiCssBaseline: {
            styleOverrides: {
              body: {
                transition: "background-color 0.25s ease, color 0.25s ease",
              },
              "*": {
                boxSizing: "border-box",
              },
            },
          },
          MuiPaper: {
            styleOverrides: {
              root: {
                backgroundImage: "none",
              },
            },
          },
          MuiButton: {
            styleOverrides: {
              root: {
                borderRadius: 999,
                textTransform: "none",
                fontWeight: 600,
              },
            },
          },
          MuiChip: {
            styleOverrides: {
              root: {
                borderRadius: 999,
              },
            },
          },
          MuiOutlinedInput: {
            styleOverrides: {
              root: {
                borderRadius: 12,
              },
            },
          },
        },
      }),
    [mode]
  );

  const value = useMemo(
    () => ({
      mode,
      isDark: mode === "dark",
      setMode,
      toggleTheme,
    }),
    [mode]
  );

  return (
    <ThemeModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeModeContext.Provider>
  );
}